import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import { FreeMode, Navigation } from 'swiper';
import { styled, Grid } from '@mui/material';
import { GenericContainer } from '../components/GenericContainer.js';
import { WIDGET_SPACE, TABLET_SCREEN_SIZE, WIDGET_SPACE_HALF, DESKTOP_SCREEN_SIZE, DESKTOP_HORIZONTAL_PADDING, TABLET_HORIZONTAL_PADDING, MOBILE_SCREEN_SIZE, MOBILE_HORIZONTAL_PADDING } from '../../utils/constants/Constants.js';
import numeral from 'numeral';
import { formatMoney } from '../../utils/stringUtils.js';

var _a, _b, _c, _d, _e;
var BrandclubRewardContainer = styled(GenericContainer)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            paddingTop: WIDGET_SPACE,
            paddingBottom: WIDGET_SPACE,
            paddingLeft: '5%',
            paddingRight: '5%',
            boxSizing: 'border-box',
            color: theme.palette.primary.main
        },
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            backgroundColor: '#fff',
            padding: "".concat(WIDGET_SPACE_HALF, "px ").concat(0, "px"),
        },
        _b);
});
var Wrapper = styled('div')({
    width: '100%',
    maxWidth: DESKTOP_SCREEN_SIZE - DESKTOP_HORIZONTAL_PADDING * 2,
    position: 'relative',
    '.swiper': (_a = {
            position: 'static'
        },
        _a["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            padding: "".concat(0, "px ").concat(TABLET_HORIZONTAL_PADDING, "px"),
        },
        _a["@media screen and (max-width: ".concat(MOBILE_SCREEN_SIZE, "px)")] = {
            padding: "".concat(0, "px ").concat(MOBILE_HORIZONTAL_PADDING, "px"),
        },
        _a),
    '.swiper-button-prev': (_b = {},
        _b["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            display: 'none',
        },
        _b.background = "url(https://media.brandclub.com/brandclub/image_asset/arrow_left_blue.svg)",
        _b.backgroundSize = 'contain',
        _b.backgroundRepeat = 'no-repeat',
        _b.backgroundPosition = 'center',
        _b.width = 40,
        _b.height = 40,
        _b.left = -42,
        _b['::after'] = {
            content: '""',
        },
        _b),
    '.swiper-button-next': (_c = {},
        _c["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            display: 'none',
        },
        _c.background = "url(https://media.brandclub.com/brandclub/image_asset/arrow_right_blue.svg)",
        _c.backgroundSize = 'contain',
        _c.backgroundRepeat = 'no-repeat',
        _c.backgroundPosition = 'center',
        _c.width = 40,
        _c.height = 40,
        _c.right = -42,
        _c['::after'] = {
            content: '""',
        },
        _c),
    '.swiper-button-disabled': {
        display: 'none',
    },
});
var RewardCard = styled(Grid)({
    userSelect: 'none',
    border: '1px solid #e8e8e8',
    padding: '30px 22px',
    borderRadius: 10,
    width: 273,
    minWidth: 273,
    '.amount': (_d = {
            fontSize: 36,
            fontWeight: 700
        },
        _d["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            fontSize: 26,
        },
        _d),
    '.title': {
        fontSize: 20,
        fontWeight: 700,
    },
    '.desc': (_e = {
            fontSize: 18,
            fontWeight: 500
        },
        _e["@media screen and (max-width: ".concat(TABLET_SCREEN_SIZE, "px)")] = {
            fontSize: 16,
        },
        _e),
});
var RewardBox = function (_a) {
    var amount = _a.amount, title = _a.title, desc = _a.desc;
    return (React.createElement(RewardCard, null,
        React.createElement("div", { className: "amount" }, amount),
        React.createElement("div", { className: "title" }, title),
        React.createElement("div", { className: "desc" }, desc)));
};
var hasRewardAmount = function (rewardAmount) {
    return (rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardAmount) > 0 || (rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardPercent) > 0;
};
var formatRewardAmount = function (rewardAmount, includeDecimal) {
    if (includeDecimal === void 0) { includeDecimal = false; }
    if ((rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardAmountType) === 'percent') {
        return numeral((rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardPercent) / 100.0).format("0,0%");
    }
    return formatMoney(rewardAmount === null || rewardAmount === void 0 ? void 0 : rewardAmount.rewardAmount, '$', includeDecimal);
};
var BrandclubRewardCarousel = function (_a) {
    var _b;
    var brandEntity = _a.brandEntity;
    var rewardAmounts = (_b = brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.entity) === null || _b === void 0 ? void 0 : _b.rewardAmounts;
    var hasPurchaseReward = hasRewardAmount(rewardAmounts === null || rewardAmounts === void 0 ? void 0 : rewardAmounts.purchase);
    var hasSurveyReward = hasRewardAmount(rewardAmounts === null || rewardAmounts === void 0 ? void 0 : rewardAmounts.survey);
    var hasJoinReward = hasRewardAmount(rewardAmounts === null || rewardAmounts === void 0 ? void 0 : rewardAmounts.join);
    var hasReferralReward = hasRewardAmount(rewardAmounts === null || rewardAmounts === void 0 ? void 0 : rewardAmounts.customerReferral);
    var hasAffiliateReward = hasRewardAmount(rewardAmounts === null || rewardAmounts === void 0 ? void 0 : rewardAmounts.affiliate);
    if (!hasPurchaseReward && !hasPurchaseReward && !hasJoinReward && !hasReferralReward && !hasAffiliateReward) {
        return null;
    }
    return (React.createElement(BrandclubRewardContainer, null,
        React.createElement(Wrapper, null,
            React.createElement(Swiper, { freeMode: true, slidesPerView: 'auto', spaceBetween: 20, navigation: true, modules: [FreeMode, Navigation], className: "mySwiper", style: { cursor: 'grab' } },
                hasPurchaseReward && (React.createElement(SwiperSlide, { style: { width: 273 } },
                    React.createElement(RewardBox, { amount: formatRewardAmount(rewardAmounts.purchase), title: "Purchase rewards", desc: "Earn up to ".concat(formatRewardAmount(rewardAmounts.purchase), " cash back on purchases.") }))),
                hasSurveyReward && (React.createElement(SwiperSlide, { style: { width: 273 } },
                    React.createElement(RewardBox, { amount: formatRewardAmount(rewardAmounts.survey), title: "Survey rewards", desc: "Earn up to ".concat(formatRewardAmount(rewardAmounts.survey), " for every survey you complete.") }))),
                hasJoinReward && (React.createElement(SwiperSlide, { style: { width: 273 } },
                    React.createElement(RewardBox, { amount: formatRewardAmount(rewardAmounts.join), title: "Membership rewards", desc: "Earn a ".concat(formatRewardAmount(rewardAmounts.join), " reward when you join.") }))),
                hasReferralReward && (React.createElement(SwiperSlide, { style: { width: 273 } },
                    React.createElement(RewardBox, { amount: formatRewardAmount(rewardAmounts.customerReferral), title: "Referral rewards", desc: "Earn up to ".concat(formatRewardAmount(rewardAmounts.customerReferral), " for each friend you refer to join.") }))),
                hasAffiliateReward && (React.createElement(SwiperSlide, { style: { width: 273 } },
                    React.createElement(RewardBox, { amount: formatRewardAmount(rewardAmounts.affiliate), title: "Affiliate rewards", desc: "Earn a commission when you refer sales." })))))));
};

export { BrandclubRewardCarousel };
//# sourceMappingURL=index.js.map
